import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, PersonRemove } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

function employeeLabel(user) {
  if (!user) return '—';
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '—';
}

/**
 * HR Manager Teams — assign department managers and their employees.
 * Enforcement is controlled by Client Settings → Staff details → Enforce manager teams.
 */
function ManagerTeamsPanel({
  employees = [],
  canManageManagerTeams,
  enforceManagerTeams,
  department,
  departments = []
}) {
  const { showToast } = useToast();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState({
    department: '',
    managerUserId: '',
    employeeIds: [],
    remarks: ''
  });
  const [editEmployeeIds, setEditEmployeeIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (department) params.department = department;
      const res = await axios.get('/api/hrms/manager-teams', { params });
      setTeams(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load manager teams', 'error');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [department, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const managersByDept = useMemo(() => {
    const map = {};
    for (const e of employees) {
      if (!e.department) continue;
      // Prefer users who look like managers (role contains MANAGER / INSPECTOR / COFS / etc.)
      const role = e.role || '';
      const isMgr = /MANAGER|INSPECTOR|COFS|SUPER_ADMIN|ACCOUNTABLE|HR_MANAGER/i.test(role);
      if (!isMgr) continue;
      if (!map[e.department]) map[e.department] = [];
      map[e.department].push(e);
    }
    return map;
  }, [employees]);

  const employeesInDept = (dept) => employees.filter((e) => e.department === dept);

  const openCreate = () => {
    const dept = department || departments[0] || '';
    setCreateForm({
      department: dept,
      managerUserId: '',
      employeeIds: [],
      remarks: ''
    });
    setCreateOpen(true);
  };

  const openEdit = async (team) => {
    try {
      const res = await axios.get(`/api/hrms/manager-teams/${team._id}`);
      setSelected(res.data);
      setEditEmployeeIds(
        (res.data.employeeIds || []).map((u) => String(u._id || u))
      );
      setEditOpen(true);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to load team', 'error');
    }
  };

  const onCreate = async () => {
    if (!createForm.department || !createForm.managerUserId) {
      showToast('Department and manager are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await axios.post('/api/hrms/manager-teams', {
        department: createForm.department,
        managerUserId: createForm.managerUserId,
        employeeIds: createForm.employeeIds,
        remarks: createForm.remarks || undefined
      });
      showToast('Manager team created', 'success');
      setCreateOpen(false);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Create failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onSaveEmployees = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`/api/hrms/manager-teams/${selected._id}`, {
        employeeIds: editEmployeeIds
      });
      showToast('Team updated', 'success');
      setEditOpen(false);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onRemoveEmployee = async (userId) => {
    if (!selected) return;
    try {
      const res = await axios.delete(
        `/api/hrms/manager-teams/${selected._id}/employees/${userId}`
      );
      setSelected(res.data);
      setEditEmployeeIds(
        (res.data.employeeIds || []).map((u) => String(u._id || u))
      );
      showToast('Employee removed from team', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Remove failed', 'error');
    }
  };

  const toggleActive = async (team) => {
    try {
      await axios.put(`/api/hrms/manager-teams/${team._id}`, {
        isActive: !team.isActive
      });
      showToast(team.isActive ? 'Team deactivated' : 'Team activated', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || 'Update failed', 'error');
    }
  };

  const managerOptions = managersByDept[createForm.department] || [];
  const employeeOptions = employeesInDept(createForm.department);

  return (
    <Box>
      <Alert severity={enforceManagerTeams ? 'warning' : 'info'} sx={{ mb: 2 }}>
        {enforceManagerTeams
          ? 'Enforcement is ON: department managers can only manage employees assigned to their team. Configure teams below (Client Settings → Staff details controls the switch).'
          : 'Enforcement is OFF: you can still set up manager teams for organization. Turn on “Enforce manager teams” in Client Settings when ready.'}
      </Alert>

      {!canManageManagerTeams && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only Super Admin or HR Manager can create or edit manager teams.
        </Alert>
      )}

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        {canManageManagerTeams && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Create manager team
          </Button>
        )}
        <Button onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Department</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Employees</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team) => (
              <TableRow key={team._id} hover>
                <TableCell>{team.department}</TableCell>
                <TableCell>{employeeLabel(team.managerUserId)}</TableCell>
                <TableCell>
                  {(team.employeeIds || []).length
                    ? (team.employeeIds || []).map((u) => (
                      <Chip
                        key={u._id || u}
                        size="small"
                        label={employeeLabel(u)}
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={team.isActive ? 'Active' : 'Inactive'}
                    color={team.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {canManageManagerTeams && (
                      <>
                        <Button size="small" onClick={() => openEdit(team)}>
                          Edit employees
                        </Button>
                        <Button size="small" onClick={() => toggleActive(team)}>
                          {team.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!teams.length && (
              <TableRow>
                <TableCell colSpan={5}>
                  {loading
                    ? 'Loading…'
                    : 'No manager teams yet — HR can create one per department manager'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create manager team</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={createForm.department}
                onChange={(e) => setCreateForm({
                  ...createForm,
                  department: e.target.value,
                  managerUserId: '',
                  employeeIds: []
                })}
              >
                {departments.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Manager</InputLabel>
              <Select
                label="Manager"
                value={createForm.managerUserId}
                onChange={(e) => setCreateForm({ ...createForm, managerUserId: e.target.value })}
              >
                {managerOptions.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {employeeLabel(m)} ({(m.role || '').replace(/_/g, ' ')})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="team-emps-label">Employees (optional)</InputLabel>
              <Select
                labelId="team-emps-label"
                multiple
                value={createForm.employeeIds}
                onChange={(e) => setCreateForm({ ...createForm, employeeIds: e.target.value })}
                input={<OutlinedInput label="Employees (optional)" />}
                renderValue={(selectedIds) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedIds.map((id) => {
                      const emp = employeeOptions.find((e) => String(e._id) === String(id));
                      return <Chip key={id} size="small" label={emp ? employeeLabel(emp) : id} />;
                    })}
                  </Box>
                )}
              >
                {employeeOptions
                  .filter((e) => String(e._id) !== String(createForm.managerUserId))
                  .map((e) => (
                    <MenuItem key={e._id} value={e._id}>
                      <Checkbox
                        checked={createForm.employeeIds.map(String).includes(String(e._id))}
                      />
                      <ListItemText
                        primary={employeeLabel(e)}
                        secondary={(e.role || '').replace(/_/g, ' ')}
                      />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="Remarks"
              fullWidth
              value={createForm.remarks}
              onChange={(e) => setCreateForm({ ...createForm, remarks: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreate} disabled={saving}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Edit employees
          {selected ? ` · ${employeeLabel(selected.managerUserId)} (${selected.department})` : ''}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="edit-emps-label">Employees</InputLabel>
                <Select
                  labelId="edit-emps-label"
                  multiple
                  value={editEmployeeIds}
                  onChange={(e) => setEditEmployeeIds(e.target.value)}
                  input={<OutlinedInput label="Employees" />}
                  renderValue={(selectedIds) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedIds.map((id) => {
                        const emp = employeesInDept(selected.department)
                          .find((e) => String(e._id) === String(id));
                        return (
                          <Chip key={id} size="small" label={emp ? employeeLabel(emp) : id} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {employeesInDept(selected.department)
                    .filter((e) => String(e._id) !== String(selected.managerUserId?._id || selected.managerUserId))
                    .map((e) => (
                      <MenuItem key={e._id} value={String(e._id)}>
                        <Checkbox checked={editEmployeeIds.map(String).includes(String(e._id))} />
                        <ListItemText
                          primary={employeeLabel(e)}
                          secondary={(e.role || '').replace(/_/g, ' ')}
                        />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Typography variant="subtitle2">Current members</Typography>
              <Table size="small">
                <TableBody>
                  {(selected.employeeIds || []).map((u) => (
                    <TableRow key={u._id || u}>
                      <TableCell>{employeeLabel(u)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<PersonRemove />}
                          onClick={() => onRemoveEmployee(u._id || u)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(selected.employeeIds || []).length && (
                    <TableRow>
                      <TableCell colSpan={2}>No employees yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSaveEmployees} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ManagerTeamsPanel;
